import { handleApiResponse } from '../common';
import { getApiBaseUrl, API_VERSION } from '../index';

/*
  The permissionApi object provides functions to call the backend endpoints:
   • Groups: listGroups, createGroup, updateGroup, deleteGroup
   • User Groups: listUserGroups, assignUserToGroup, removeUserFromGroup
   • Plugin Access (group level): listPluginAccess, grantPluginAccess, revokePluginAccess
   • Plugin User Access (individual overrides): listPluginUserAccess, updatePluginUserAccess, removePluginUserAccess
   • Access Requests: listAccessRequests, processAccessRequest
   • Helper Endpoints: listUsers, listPlugins
*/

export const permissionApi = {
  // ----- Permission Groups -----
  async listGroups() {
    const response = await fetch(`${getApiBaseUrl()}${API_VERSION}/permissions/groups`, {
      method: 'GET',
      headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' },
      credentials: 'include',
    });
    await handleApiResponse(response);
    const data = await response.json();
    return data.groups || data;
  },

  async createGroup(group) {
    const response = await fetch(`${getApiBaseUrl()}${API_VERSION}/permissions/groups`, {
      method: 'POST',
      headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(group),
    });
    await handleApiResponse(response);
    return response.json();
  },

  async updateGroup(groupId, group) {
    const response = await fetch(`${getApiBaseUrl()}${API_VERSION}/permissions/groups/${groupId}`, {
      method: 'PATCH',
      headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(group),
    });
    await handleApiResponse(response);
    return response.json();
  },

  async deleteGroup(groupId) {
    const response = await fetch(`${getApiBaseUrl()}${API_VERSION}/permissions/groups/${groupId}`, {
      method: 'DELETE',
      headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' },
      credentials: 'include',
    });
    await handleApiResponse(response);
    return response.json();
  },

  // ----- User Groups -----
  async listUserGroups() {
    const response = await fetch(`${getApiBaseUrl()}${API_VERSION}/permissions/user-groups`, {
      method: 'GET',
      headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' },
      credentials: 'include',
    });
    await handleApiResponse(response);
    const data = await response.json();
    return data.user_groups || data;
  },

  async assignUserToGroup(groupId, userId, existingUserIds = []) {
    // Convert IDs to numbers and deduplicate them.
    const userIdNum = Number(userId);
    const dedupedUserIds = Array.from(new Set([...existingUserIds.map(Number), userIdNum]));
    const payload = {
      group_id: groupId,
      user_ids: dedupedUserIds,
    };
    console.log('Sending payload:', payload);
    
    const response = await fetch(`${getApiBaseUrl()}${API_VERSION}/permissions/user-groups`, {
      method: 'POST',
      headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(payload)
    });
    await handleApiResponse(response);
    return response.json();
  },

  async removeUserFromGroup(userId, groupId) {
    const response = await fetch(`${getApiBaseUrl()}${API_VERSION}/permissions/user-groups`, {
      method: 'DELETE',
      headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ user_id: userId, group_id: groupId }),
    });
    await handleApiResponse(response);
    return response.json();
  },

  // ----- Plugin Access (Group Level) -----
  async listPluginAccess() {
    const response = await fetch(`${getApiBaseUrl()}${API_VERSION}/permissions/plugin-access`, {
      method: 'GET',
      headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' },
      credentials: 'include',
    });
    await handleApiResponse(response);
    const data = await response.json();
    return data.plugin_access || data;
  },

  async grantPluginAccess(groupId, pluginId) {
    const response = await fetch(`${getApiBaseUrl()}${API_VERSION}/permissions/plugin-access`, {
      method: 'POST',
      headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ group_id: groupId, plugin_id: pluginId }),
    });
    await handleApiResponse(response);
    return response.json();
  },

  async revokePluginAccess(groupId, pluginId) {
    const response = await fetch(`${getApiBaseUrl()}${API_VERSION}/permissions/plugin-access`, {
      method: 'DELETE',
      headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ group_id: groupId, plugin_id: pluginId }),
    });
    await handleApiResponse(response);
    return response.json();
  },

  // ----- Plugin User Access (Individual Overrides) -----
  async listPluginUserAccess() {
    const response = await fetch(`${getApiBaseUrl()}${API_VERSION}/permissions/plugin-user-access`, {
      method: 'GET',
      headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' },
      credentials: 'include',
    });
    await handleApiResponse(response);
    const data = await response.json();
    return data.plugin_user_access || data;
  },

  async updatePluginUserAccess(userId, pluginId, granted) {
    const response = await fetch(`${getApiBaseUrl()}${API_VERSION}/permissions/plugin-user-access`, {
      method: 'POST', // or PATCH if your backend differentiates create vs update
      headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ user_id: userId, plugin_id: pluginId, granted }),
    });
    await handleApiResponse(response);
    return response.json();
  },

  async removePluginUserAccess(userId, pluginId) {
    const response = await fetch(`${getApiBaseUrl()}${API_VERSION}/permissions/plugin-user-access`, {
      method: 'DELETE',
      headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ user_id: userId, plugin_id: pluginId, granted: false }),
    });
    await handleApiResponse(response);
    return response.json();
  },

  // ----- Access Requests -----
  async listAccessRequests() {
    const response = await fetch(`${getApiBaseUrl()}${API_VERSION}/permissions/access-requests`, {
      method: 'GET',
      headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' },
      credentials: 'include',
    });
    await handleApiResponse(response);
    const data = await response.json();
    return data.access_requests || data;
  },

  async processAccessRequest(requestId, status) {
    const response = await fetch(`${getApiBaseUrl()}${API_VERSION}/permissions/access-requests/${requestId}`, {
      method: 'PATCH',
      headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ status }),
    });
    await handleApiResponse(response);
    return response.json();
  },

  // ----- Helper Endpoints (Users & Plugins) -----
  async listUsers() {
    const response = await fetch(`${getApiBaseUrl()}${API_VERSION}/users`, {
      method: 'GET',
      headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' },
      credentials: 'include',
    });
    await handleApiResponse(response);
    const data = await response.json();
    return data.users || data;
  },

  async listPlugins() {
    const response = await fetch(`${getApiBaseUrl()}${API_VERSION}/plugins/list`, {
      method: 'GET',
      headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' },
      credentials: 'include',
    });
    await handleApiResponse(response);
    const data = await response.json();
    return data.plugins || data;
  },

  // ----- Permission Check & Access Request Submission -----
  async checkPermission(appId) {
    const url = `${getApiBaseUrl()}${API_VERSION}/permissions/check/${appId}`;
    console.log("[checkPermission] Checking permission for appId:", appId, "via URL:", url);
    
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      },
      credentials: 'include',
    });
    console.log("[checkPermission] HTTP status:", response.status, response.statusText);
    await handleApiResponse(response);
    const data = await response.json();
    console.log("[checkPermission] Permission data received:", data);

    return data;
  },

  async createAccessRequest(pluginId, message = "") {
    const payload = {
      plugin_id: pluginId,
      message,
    };
    console.log("Submitting access request:", payload);
    
    // Uncomment this block when backend integration is complete:
    /*
    const response = await fetch(`${getApiBaseUrl()}${API_VERSION}/permissions/access-requests`, {
      method: 'POST',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      },
      credentials: 'include',
      body: JSON.stringify(payload),
    });
    await handleApiResponse(response);
    return response.json();
    */
    
    // For now, return a simulated response:
    return Promise.resolve({
      request_id: Date.now(),
      user_id: 999,  // Replace with actual user ID from session when available.
      plugin_id: pluginId,
      message,
      status: "PENDING",
      created_at: new Date().toISOString(),
      processed_by: null,
      processed_at: null,
    });
  },
};

// --- Debugging: Print Detailed Permission Data ---
// This method is for debugging purposes only. It prints out permission details,
// all groups, and the current user's groups.
permissionApi.debugPermissionDetails = async (appId) => {
  console.log("[Debug] Starting detailed permissions debugging for appId:", appId);
  try {
    const permissionData = await permissionApi.checkPermission(appId);
    console.log("[Debug] Permission Data:", permissionData);
  } catch (e) {
    console.error("[Debug] Error fetching permission data:", e);
  }
  try {
    const groupsData = await permissionApi.listGroups();
    console.log("[Debug] All Groups:", groupsData);
  } catch (e) {
    console.error("[Debug] Error fetching groups:", e);
  }
  try {
    const userGroupsData = await permissionApi.listUserGroups();
    console.log("[Debug] Current User Groups:", userGroupsData);
  } catch(e) {
    console.error("[Debug] Error fetching user groups:", e);
  }
};

export default permissionApi;